import { getApiUrl } from "../api";
import { useEffect, useMemo, useState } from "react";
import Preloader from "../component/Preloader";
import Header from "../component/Header";
import LanguagePopup from "../component/LanguagePopup";
import Footer from "../component/Footer";
import FloatingWidgets from "../component/FloatingWidgets";
import HomeModals from "../component/HomeModals";
import useHomeInteractions from "../component/useHomeInteractions";
import { Link } from "../component/AppLink";
import { getCloudflareImageUrl } from "../utils/cloudflareImages";
import { ArabicFooter, ArabicHeader } from "./ArabicHome";



const getJwtToken = () => {
  const keys = ["token", "accessToken", "jwt", "authToken"];
  for (const key of keys) {
    const token = localStorage.getItem(key) || sessionStorage.getItem(key);
    if (token?.trim()) return token.replace(/^Bearer\s+/i, "").trim();
  }

  return (process.env.REACT_APP_JWT_TOKEN || "").replace(/^Bearer\s+/i, "").trim();
};


const stripHtml = (value) => String(value || "").replace(/<[^>]*>/g, " ").replace(/\s+/g, " ").trim();

const formatNewsDate = (value, language = "en") => {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return stripHtml(value);
  return date.toLocaleDateString(language === "ar" ? "ar-AE" : "en-US", { year: "numeric", month: "short", day: "2-digit" });
};

const mapNewsDetails = (item, language = "en") => {
  if (!item) return {};
  const isArabic = language === "ar";
  return {
    id: item.id || "",
    title: stripHtml((isArabic ? (item.title_ar || item.Title_ar || item.titleAr || item.TitleAr) : null) || item.title_en || item.Title_en || item.title || item.Title),
    date: formatNewsDate(item.dt || item.date, language),
    description: (isArabic ? (item.description_ar || item.Description_ar || item.descriptionAr || item.DescriptionAr) : null) || item.description_en || item.Description_en || item.description || item.Description || "",
    image: getCloudflareImageUrl(item.ImagePath || item.image, "w=1200,sharpen=1"),
  };
};

const getNewsDescriptionMarkup = (description) => {
  const value = String(description || "").trim();
  if (!value) return "";

  if (/<(p|ul|ol|blockquote|h[1-6]|div|table|figure|img)\b/i.test(value)) {
    return value;
  }

  return `<p class="wow fadeInUp">${value}</p>`;
};

const buildContentWithInlineGallery = (description) => {
  const markup = getNewsDescriptionMarkup(description);

  if (typeof window === "undefined" || typeof window.DOMParser === "undefined") {
    return { contentHtml: markup, images: [] };
  }

  const doc = new window.DOMParser().parseFromString(`<div>${markup}</div>`, "text/html");
  const root = doc.body.firstElementChild;
  const images = [];

  Array.from(root.querySelectorAll("img")).forEach((image) => {
    const imageData = {
      src: image.getAttribute("src") || "",
      alt: image.getAttribute("alt") || `News gallery image ${images.length + 1}`,
    };

    if (!imageData.src) {
      image.remove();
      return;
    }

    const index = images.push(imageData) - 1;
    const gallery = doc.createElement("span");
    gallery.className = "content-gallery content-gallery-inline";

    const button = doc.createElement("button");
    button.className = "content-gallery-item";
    button.type = "button";
    button.setAttribute("data-gallery-index", String(index));

    const thumbnail = doc.createElement("img");
    thumbnail.src = imageData.src;
    thumbnail.alt = imageData.alt;

    button.appendChild(thumbnail);
    gallery.appendChild(button);
    image.replaceWith(gallery);
  });

  root.querySelectorAll("p, figure, div").forEach((element) => {
    if (!element.textContent.trim() && !element.querySelector("img, video, iframe, .content-gallery")) {
      element.remove();
    }
  });

  return { contentHtml: root.innerHTML, images };
};

export default function NewsDetails({ language = "en" }) {
  const isArabic = language === "ar";
  const interactions = useHomeInteractions();
  const newsId = useMemo(() => new URLSearchParams(window.location.search).get("id") || "", []);
  const [news, setNews] = useState({});
  const [loadError, setLoadError] = useState("");
  const [activeGalleryIndex, setActiveGalleryIndex] = useState(null);
  const galleryContent = useMemo(() => buildContentWithInlineGallery(news.description), [news.description]);
  const activeImage = activeGalleryIndex !== null ? galleryContent.images[activeGalleryIndex] : null;

  useEffect(() => {
    document.title = `${news.title || (isArabic ? "أخبار تراحم" : "Tarahum News")} | ${isArabic ? "مؤسسة تراحم الخيرية" : "Tarahum Charity Foundation"}`;
    if (!isArabic) return undefined;
    document.documentElement.lang = "ar";
    document.documentElement.dir = "rtl";
    document.body.classList.add("arabic-layout-page");
    return () => {
      document.documentElement.lang = "en";
      document.documentElement.dir = "ltr";
      document.body.classList.remove("arabic-layout-page");
    };
  }, [isArabic, news.title]);

  useEffect(() => {
    const closeOnEscape = (event) => {
      if (event.key === "Escape") {
        setActiveGalleryIndex(null);
      }
    };

    document.addEventListener("keydown", closeOnEscape);
    return () => document.removeEventListener("keydown", closeOnEscape);
  }, []);

  const showPreviousImage = () => {
    setActiveGalleryIndex((current) => {
      if (current === null || !galleryContent.images.length) return current;
      return current === 0 ? galleryContent.images.length - 1 : current - 1;
    });
  };

  const showNextImage = () => {
    setActiveGalleryIndex((current) => {
      if (current === null || !galleryContent.images.length) return current;
      return current === galleryContent.images.length - 1 ? 0 : current + 1;
    });
  };

  useEffect(() => {
    const controller = new AbortController();

    const loadNewsDetails = async () => {
      try {
        setLoadError("");

        const headers = { Accept: "application/json" };
        const token = getJwtToken();
        if (token) headers.Authorization = `Bearer ${token}`;

        const response = await fetch(getApiUrl(`/api/GetNewsDetails.ashx?id=${encodeURIComponent(newsId)}`), {
          headers,
          credentials: "include",
          signal: controller.signal,
        });

        if (!response.ok) throw new Error(`News details request failed with ${response.status}`);

        const result = await response.json();
        const detail = Array.isArray(result?.data) && result.data.length ? mapNewsDetails(result.data[0], language) : {};
        setNews(detail);
      } catch (error) {
        if (error.name !== "AbortError") {
          setLoadError(isArabic ? "تعذر تحميل تفاصيل الخبر." : "Unable to load news details.");
          setNews({});
        }
      }
    };

    loadNewsDetails();

    return () => controller.abort();
  }, [newsId, language, isArabic]);

  return (
    <>
      <Preloader />
      {isArabic ? <ArabicHeader /> : <><Header /><LanguagePopup /></>}

      <section className="kid-d-wrp pb-0">
        <div className="kid-container">
          <div className="kid-hero">
            <h1>{isArabic ? "أخبار تراحم" : "Tarahum News"}</h1>
            <ul className="kid-breadcrumb">
              <li><Link to="/">{isArabic ? "الرئيسية" : "home"}</Link></li>
              <li>/</li>
              <li><Link to="/news">{isArabic ? "أخبار تراحم" : "Tarahum News"}</Link></li>
            </ul>
          </div>
        </div>
      </section>

      <div className={`page-single-post${isArabic ? " arabic-news-details-page" : ""}`} dir={isArabic ? "rtl" : undefined}>
        <div className="container">
          {loadError ? <div className="donation-error">{loadError}</div> : null}
          <div className="row">
            <div className="col-lg-12">
              <div className="post-image">
                <figure className="image-anime">
                  <img src={news.image} alt={news.title} />
                </figure>
              </div>
              <div className="post-content">
                <div className="post-entry">
                  <h5>{news.title}</h5>
                  <div
                    onClick={(event) => {
                      const trigger = event.target.closest?.("[data-gallery-index]");
                      if (!trigger) return;
                      setActiveGalleryIndex(Number(trigger.getAttribute("data-gallery-index")));
                    }}
                    dangerouslySetInnerHTML={{ __html: galleryContent.contentHtml }}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {activeImage ? (
        <div className="content-gallery-lightbox" role="dialog" aria-modal="true" aria-label={isArabic ? "معاينة صورة الخبر" : "News image preview"}>
          <button className="content-gallery-backdrop" type="button" aria-label={isArabic ? "إغلاق المعاينة" : "Close preview"} onClick={() => setActiveGalleryIndex(null)}></button>
          <div className="content-gallery-modal">
            <button className="content-gallery-close" type="button" aria-label={isArabic ? "إغلاق المعاينة" : "Close preview"} onClick={() => setActiveGalleryIndex(null)}>
              <i className="fa-solid fa-xmark"></i>
            </button>
            {galleryContent.images.length > 1 ? (
              <>
                <button className="content-gallery-nav content-gallery-prev" type="button" aria-label={isArabic ? "الصورة السابقة" : "Previous image"} onClick={showPreviousImage}>
                  <i className="fa-solid fa-chevron-left"></i>
                </button>
                <button className="content-gallery-nav content-gallery-next" type="button" aria-label={isArabic ? "الصورة التالية" : "Next image"} onClick={showNextImage}>
                  <i className="fa-solid fa-chevron-right"></i>
                </button>
              </>
            ) : null}
            <img src={activeImage.src} alt={activeImage.alt} />
          </div>
        </div>
      ) : null}

      {isArabic ? <ArabicFooter /> : <Footer />}
      <FloatingWidgets {...interactions} />
      <HomeModals {...interactions} />
    </>
  );
}
